<template>
  <div ref="containerRef" class="w-full">
    <div v-if="!useDropdown" class="flex">
      <button
        v-for="option in props.context.options"
        :key="option.value"
        :class="
          twMerge(
            'inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap px-4 py-2 text-sm font-medium ring-offset-background transition-colors first:rounded-l-md last:rounded-r-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
            props.context._value === option.value
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
          )
        "
        @click="props.context.node.input(option.value)"
        type="button"
        :aria-current="props.context._value === option.value ? true : undefined"
      >
        {{ option.label }}
      </button>
    </div>

    <div v-else class="relative">
      <button
        @click="dropdownOpen = !dropdownOpen"
        :class="
          twMerge(
            'inline-flex h-10 w-full items-center justify-between gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
          )
        "
        type="button"
        :aria-expanded="dropdownOpen"
      >
        <span>{{ selectedOption?.label || "Select option" }}</span>
        <svg
          class="h-4 w-4 transition-transform"
          :class="{ 'rotate-180': dropdownOpen }"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </button>

      <div v-if="dropdownOpen" class="absolute z-50 mt-1 w-full rounded-md border border-input bg-popover shadow-lg">
        <button
          v-for="option in props.context.options"
          :key="option.value"
          @click="selectOption(option)"
          :class="
            twMerge(
              'w-full px-4 py-2 text-left text-sm transition-colors first:rounded-t-md last:rounded-b-md hover:bg-accent hover:text-accent-foreground',
              props.context._value === option.value ? 'bg-primary text-primary-foreground' : '',
            )
          "
          type="button"
        >
          {{ option.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { twMerge } from "tailwind-merge";

const props = defineProps({
  context: {
    type: Object,
    required: true,
  },
  isFormkit: {
    type: Boolean,
    default: true,
  },
});

const containerRef = ref(null);
const useDropdown = ref(false);
const dropdownOpen = ref(false);
const resizeObserver = ref(null);

const selectedOption = computed(() => {
  return props.context.options.find((option) => option.value === props.context._value);
});

const getParentElement = () => {
  if (props.isFormkit) {
    // Formkit adds a bunch of parent elements
    return containerRef.value.parentElement.parentElement.parentElement.parentElement;
  }
  return containerRef.value.parentElement;
};

const calculateMinWidth = () => {
  if (!containerRef.value || !props.context.options) return 0;

  // Create a temporary element to measure text width
  const tempElement = document.createElement("span");
  tempElement.style.visibility = "hidden";
  tempElement.style.position = "absolute";
  tempElement.style.whiteSpace = "nowrap";
  tempElement.style.fontSize = "14px"; // text-sm
  tempElement.style.fontWeight = "500"; // font-medium
  tempElement.style.fontFamily = getComputedStyle(containerRef.value).fontFamily;

  document.body.appendChild(tempElement);

  let totalWidth = 0;

  props.context.options.forEach((option) => {
    tempElement.textContent = option.label;
    const textWidth = tempElement.offsetWidth;
    // Add padding (px-4 = 32px) + gap (8px) + border (2px)
    totalWidth += textWidth + 42;
  });

  document.body.removeChild(tempElement);

  return totalWidth;
};

const checkLayout = () => {
  if (!containerRef.value) return;

  const parentElement = getParentElement();
  const availableWidth = parentElement ? parentElement.offsetWidth : containerRef.value.offsetWidth;

  const parentStyles = parentElement ? getComputedStyle(parentElement) : {};
  const parentPadding = parseFloat(parentStyles.paddingLeft || 0) + parseFloat(parentStyles.paddingRight || 0);

  const effectiveWidth = availableWidth - parentPadding;
  const minWidth = calculateMinWidth();

  useDropdown.value = effectiveWidth < minWidth;
};

const selectOption = (option) => {
  props.context.node.input(option.value);
  dropdownOpen.value = false;
};

const handleClickOutside = (event) => {
  if (containerRef.value && !containerRef.value.contains(event.target)) {
    dropdownOpen.value = false;
  }
};

onMounted(() => {
  checkLayout();

  if (window.ResizeObserver) {
    resizeObserver.value = new ResizeObserver(() => {
      checkLayout();
    });

    const parentElement = getParentElement();
    if (parentElement) {
      resizeObserver.value.observe(parentElement);
    } else {
      resizeObserver.value.observe(containerRef.value);
    }
  }

  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  if (resizeObserver.value) {
    resizeObserver.value.disconnect();
  }
  document.removeEventListener("click", handleClickOutside);
});
</script>
